import React, { useEffect, useRef, useState } from "react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import "./EpisodeScreen.css"
import Nav from "../components/Nav"
import CosmosBackground from "../components/CosmosBackground"
import { useNavigate, useParams } from "react-router-dom"
import ReactPlayer from "react-player"
import requests from "../api/requests"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import { Skeleton } from "@mui/material"
import { handleAPIError } from "../api/error"
import { useSnackbar } from "react-simple-snackbar"
import { useDispatch } from "react-redux"
import { errorSnackBar } from "../components/snackBarOptions"
import { usePlayerPreferences } from "../utils/usePlayerPreferences"

const baseURL = process.env.REACT_APP_CMS_URL

function EpisodeScreen() {
  const [lastPlayedValue, setLastPlayedValue] = useState(0.0)
  const [playerKey, setPlayerKey] = useState(0)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef(null)
  const playerRef = useRef()

  const [content, setContent] = useState({
    title: "",
    description: "",
    attachment: null,
  })
  const [meta, setMeta] = useState({
    watched: false,
    percentage_watched: 0.0,
  })

  const [isLoading, setIsLoading] = useState(true)
  const { id } = useParams()

  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { onPlayerReady } = usePlayerPreferences(playerRef)

  useEffect(() => {
    retryCountRef.current = 0

    const fetchEpisode = async () => {
      try {
        const response = await requests.getEpisode(id)
        setContent(response.data?.episode)
        setMeta(response.data?.meta)
        setPlayerKey(0)
      } catch (error) {
        handleAPIError(error, { dispatch, navigate, openErrorSnackbar })
      } finally {
        setIsLoading(false)
      }
    }

    fetchEpisode()
  }, [id, dispatch, navigate])

  useEffect(() => {
    return () => clearTimeout(retryTimerRef.current)
  }, [])

  const onError = () => {
    if (retryCountRef.current >= 2) return
    retryTimerRef.current = setTimeout(() => {
      retryCountRef.current += 1
      setPlayerKey((k) => k + 1)
    }, 2000)
  }

  const onDuration = () => {
    if (meta.watched) return
    playerRef.current.seekTo(meta?.percentage_watched || 0.0, "fraction")
  }

  const [showFullDescription, setShowFullDescription] = useState(
    content?.description?.length < 300,
  )
  const [hasShortDescription, setHasShortDescription] = useState(false)

  const description = content?.description.replaceAll(`\n`, "<br>")

  const limitedDescription = showFullDescription
    ? description
    : description?.substring(0, 300) + "..."

  const saveWatchedProgress = async (played, watch) => {
    try {
      await requests.setEpisodeWatched(content?.id, {
        watched: watch,
        percentage_watched: played,
      })
    } catch (error) {
      console.log(error)
    }
  }

  const onProgress = (state) => {
    if (state.played > lastPlayedValue + 0.1) {
      if (state.played > 0.8) {
        saveWatchedProgress(state.played, true)
        return
      }
      saveWatchedProgress(state.played, false)
      setLastPlayedValue(state.played)
    }
  }

  const onEnded = () => {
    saveWatchedProgress(1, true)
    setLastPlayedValue(1)
  }

  const openAttachment = (url) => {
    window.open(`${baseURL}${url}`, "_blank")
  }

  const getFilesize = (size) => {
    const kbSize = size / 1024
    if (kbSize < 1024) {
      return `${kbSize.toFixed(2)} KB`
    }

    return `${(kbSize / 1024).toFixed(2)} MB`
  }

  const skeletonLoader = (
    <div className="episodeScreen__body">
      <Skeleton variant="rounded" width={90} height={34} style={{ borderRadius: 20, marginBottom: 16 }} />
      <div className="episodeScreen__playerContainer">
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ bgcolor: "#333333", transform: "none" }} />
      </div>
      <div className="episodeScreen__info">
        <Skeleton variant="text" width="55%" height={32} />
      </div>
      <Skeleton variant="rounded" width="100%" height={80} style={{ borderRadius: 10 }} />
    </div>
  )

  return (
    <>
    <CosmosBackground />
    <Nav />
    <div className="episodeScreen">
      {isLoading ? (
        skeletonLoader
      ) : (
        <div className="episodeScreen__body">
          <button onClick={() => window.history.back()}>
            <div className="episodeScreen__back">
              <ArrowBackIcon /> Voltar
            </div>
          </button>
          <div className="episodeScreen__playerContainer">
            <ReactPlayer
              key={`${content?.video_hls ?? ""}-${playerKey}`}
              ref={(player) => (playerRef.current = player)}
              width="100%"
              height="100%"
              playing
              controls
              url={content?.video_hls}
              config={{
                file: {
                  hlsOptions: { autoStartLoad: true, startLevel: -1 },
                },
              }}
              onReady={onPlayerReady}
              onDuration={onDuration}
              onProgress={onProgress}
              onEnded={onEnded}
              onError={onError}
            />
          </div>
          <div className="episodeScreen__info">
            <h1>{content?.title}</h1>
          </div>
          {description.length > 0 && (
            <div className="episodeScreen__description">
              <h4 dangerouslySetInnerHTML={{ __html: limitedDescription }} />
              {!showFullDescription && (
                <button
                  onClick={() => {
                    setShowFullDescription(true)
                    setHasShortDescription(true)
                  }}
                >
                  Mostrar Mais
                  <KeyboardArrowDownIcon />
                </button>
              )}
              {hasShortDescription && (
                <button
                  onClick={() => {
                    setShowFullDescription(false)
                    setHasShortDescription(false)
                  }}
                >
                  Mostrar Menos
                  <KeyboardArrowUpIcon />
                </button>
              )}
            </div>
          )}

          {content?.attachment !== null && (
            <div
              className="episodeScreen_attachment"
              onClick={() => openAttachment(content.attachment.url)}
            >
              <PictureAsPdfIcon />
              <div className="episodeScreen_attachment__info">
                <h5 href={`${baseURL}${content?.attachment?.url}`}>
                  {content.attachment.filename}
                </h5>
                <p>{getFilesize(content.attachment.filesize)}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  )
}

export default EpisodeScreen
